import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppConfigApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/app-config/app-config-api';
import {
  AppConfigResponse,
  ShellVersionPolicyResponse,
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/app-config/app-config.response';
import { STORE_URLS } from '../../constants/store-urls.constants';
import { NativeBridgeService } from '../webview/native-bridge.service';

export type ShellUpdate = 'none' | 'recommended' | 'required';

/** Délai au-delà duquel on ouvre l'app sans attendre la réponse. */
export const APP_CONFIG_TIMEOUT_MS = 5_000;

const DISMISSED_KEY = 'vw-update-dismissed-build';

/** Ce que la coque installée doit faire, d'après la politique de sa plateforme. */
export function shellUpdateFor(build: number | null, policy: ShellVersionPolicyResponse | undefined): ShellUpdate {
  if (build === null || !policy) return 'none';
  if (isBuild(policy.minSupportedBuild) && build < policy.minSupportedBuild) return 'required';
  if (isBuild(policy.recommendedBuild) && build < policy.recommendedBuild) return 'recommended';
  return 'none';
}

function isBuild(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/**
 * Versions minimale et recommandée de la coque native (NA-03, ARC-16).
 *
 * Un déploiement web touche aussitôt tous les binaires installés : celui qui
 * n'a plus ce qu'il faut doit pouvoir être arrêté, avec une issue. Le build se
 * lit sur l'agent utilisateur (1.6+) ou s'estime aux canaux d'un ancien
 * binaire (`NativeBridgeService.build`).
 *
 * Jamais bloquant par accident : dans un navigateur rien n'est demandé, et une
 * réponse absente (back pas encore déployé : 404), lente ou mal formée vaut
 * « aucune contrainte ».
 */
@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly bridge = inject(NativeBridgeService);
  private readonly translate = inject(TranslateService);

  readonly update = signal<ShellUpdate>('none');
  /** Texte de mise à jour choisi par l'équipe (`updateMessage`), sinon celui de l'app. */
  private readonly messages = signal<AppConfigResponse['updateMessage']>(undefined);
  /** Build recommandé de la dernière réponse : la ligne refermée ne revient qu'au suivant. */
  private recommendedBuild?: number;

  async load(): Promise<void> {
    const build = this.bridge.build;
    if (build === null) return;
    let config: AppConfigResponse | null;
    try {
      config = await new AppConfigApi().getAppConfig({ timeout: APP_CONFIG_TIMEOUT_MS });
    } catch {
      return;
    }
    const policy = config?.[this.bridge.platform as 'ios' | 'android'];
    this.messages.set(config?.updateMessage);
    const update = shellUpdateFor(build, policy);
    this.recommendedBuild = policy?.recommendedBuild;
    this.update.set(update === 'recommended' && this.dismissed() ? 'none' : update);
  }

  /** Texte de l'écran et de la ligne de l'accueil dans la langue de l'app, s'il a été réglé. */
  message(): string | null {
    const lang = this.translate.getCurrentLang() as keyof NonNullable<AppConfigResponse['updateMessage']>;
    return this.messages()?.[lang] ?? null;
  }

  /** « Mettre à jour » : la fiche du magasin, hors de la WebView (canal présent dans tous les binaires). */
  openStore(): void {
    const url = this.bridge.platform === 'ios' ? STORE_URLS.appStore : STORE_URLS.playStore;
    if (!this.bridge.openExternal(url)) window.open(url, '_blank');
  }

  /** Secours de l'écran bloquant : la même app, dans le navigateur du téléphone. */
  continueInBrowser(): void {
    this.bridge.openExternal(window.location.origin);
  }

  /** Ligne refermée : plus rien jusqu'à la prochaine version recommandée. */
  dismissRecommended(): void {
    if (this.update() !== 'recommended') return;
    this.update.set('none');
    try {
      localStorage.setItem(DISMISSED_KEY, String(this.recommendedBuild));
    } catch {
      // Sans stockage, la ligne reviendra au prochain lancement : un moindre mal.
    }
  }

  private dismissed(): boolean {
    try {
      return localStorage.getItem(DISMISSED_KEY) === String(this.recommendedBuild);
    } catch {
      return false;
    }
  }
}
